import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Wallet from "@/pages/Wallet";
import About from "@/pages/About";
import PayoutToast from "@/components/PayoutToast";
import Firms from "@/pages/Firms";
import Compare from "@/pages/Compare";
import FirmDetail from "@/pages/FirmDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import MemberProfile from "@/pages/MemberProfile";
import Journal from "@/pages/Journal";
import Consistency from "@/pages/tools/Consistency";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import Support from "@/pages/Support";
import Earn from "@/pages/Earn";
import Reviews from "@/pages/Reviews";
import Promo from "@/pages/Promo";
import PromoVertical from "@/pages/PromoVertical";
import SupportChat from "@/components/SupportChat";

function useLenis() {
  const rafRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    const raf = (time) => {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    };
    rafRef.current = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lenis.destroy();
    };
  }, []);
}

function AppRouter() {
  const location = useLocation();
  // OAuth return: process session_id FIRST (synchronous check prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/firms" element={<Firms />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/firms/:slug" element={<FirmDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/earn" element={<Earn />} />
        <Route path="/about" element={<About />} />
        <Route path="/members/:userId" element={<MemberProfile />} />
        <Route path="/tools/consistency" element={<Consistency />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/support" element={<Support />} />
        <Route path="/promo" element={<Promo />} />
        <Route path="/promo-vertical" element={<PromoVertical />} />
      </Routes>
      {!location.pathname.startsWith("/admin") && !location.pathname.startsWith("/promo") && <PayoutToast />}
      <SupportChat />
    </>
  );
}

function App() {
  useLenis();
  return (
    <div className="min-h-screen bg-ink text-foreground">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" toastOptions={{
            style: { background: "#0D0A1A", border: "1px solid #251D4A", borderRadius: "12px", fontFamily: "JetBrains Mono, monospace", fontSize: "12px" },
          }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
